import React, { useState, useEffect } from 'react'; // v2 - SEOMeta owns all meta tags
import { useParams, useNavigate } from 'react-router-dom';
import supabase from './supabaseClient';
import SEOMeta from './SEOMeta';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      const { data } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('slug', slug)
        .eq('status', 'published')
        .single();
      setPost(data);
      setLoading(false);
    };
    fetchPost();
  }, [slug]);

  const renderContent = (content) => {
    if (!content) return null;
    const lines = content.trim().split('\n');
    const elements = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];

      // Detect table block
      if (line.trim().startsWith('|') && lines[i+1] && lines[i+1].includes('---')) {
        const tableLines = [];
        while (i < lines.length && lines[i].trim().startsWith('|')) {
          tableLines.push(lines[i]);
          i++;
        }
        const headers = tableLines[0].split('|').filter(c => c.trim() !== '');
        const rows = tableLines.slice(2).map(r => r.split('|').filter(c => c.trim() !== ''));
        elements.push(
          <div key={i} style={{ overflowX: 'auto', marginBottom: '20px', marginTop: '8px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Lato, sans-serif', fontSize: '14px' }}>
              <thead>
                <tr style={{ background: 'linear-gradient(135deg, #2c4a2e, #4a7c59)' }}>
                  {headers.map((h, j) => (
                    <th key={j} style={{ padding: '10px 14px', color: '#f5f0e8', textAlign: 'left', fontWeight: 700, letterSpacing: '1px', fontSize: '12px', textTransform: 'uppercase' }}>{h.trim()}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, j) => (
                  <tr key={j} style={{ background: j % 2 === 0 ? '#ffffff' : '#f9fcf9', borderBottom: '1px solid rgba(143,185,154,0.2)' }}>
                    {row.map((cell, k) => (
                      <td key={k} style={{ padding: '10px 14px', color: '#2c4a2e', verticalAlign: 'top' }}>{cell.trim()}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        continue;
      }

      if (line.startsWith('## ')) {
        elements.push(<h2 key={i} style={{ fontFamily: 'Playfair Display, serif', fontSize: '24px', color: '#1e3d20', marginTop: '36px', marginBottom: '12px' }}>{line.replace('## ', '')}</h2>);
      } else if (line.startsWith('# ')) {
        elements.push(<h1 key={i} style={{ fontFamily: 'Playfair Display, serif', fontSize: '30px', color: '#1e3d20', marginTop: '36px', marginBottom: '12px' }}>{line.replace('# ', '')}</h1>);
      } else if (line.match(/^!\[.*\]\(.*\)$/)) {
        const match = line.match(/^!\[(.*)\]\((.*)\)$/);
        if (match) {
          elements.push(<img key={i} src={match[2]} alt={match[1] || 'blog image'} style={{ width: '100%', maxHeight: '500px', objectFit: 'cover', borderRadius: '12px', margin: '16px 0', display: 'block' }} />);
        }
      } else if (line.startsWith('- ')) {
        const bulletText = line.replace(/^- /, '');
        const bulletParts = bulletText.split(/(\*\*[^*]+\*\*)/g);
        elements.push(
          <li key={i} style={{ fontFamily: 'Lato, sans-serif', fontSize: '16px', color: '#2c4a2e', marginBottom: '6px', marginLeft: '20px', lineHeight: 1.7 }}>
            {bulletParts.map((part, j) => {
              if (part.startsWith('**') && part.endsWith('**')) {
                return <strong key={j}>{part.replace(/\*\*/g, '')}</strong>;
              }
              return part;
            })}
          </li>
        );
      } else if (line === '') {
        elements.push(<div key={i} style={{ height: '12px' }} />);
      } else {
        const parts = line.split(/(\*\*[^*]+\*\*)/g);
        elements.push(
          <p key={i} style={{ fontFamily: 'Lato, sans-serif', fontSize: '16px', color: '#2c4a2e', lineHeight: 1.8, marginBottom: '8px' }}>
            {parts.map((part, j) => {
              if (part.startsWith('**') && part.endsWith('**')) {
                return <strong key={j}>{part.replace(/\*\*/g, '')}</strong>;
              }
              return part;
            })}
          </p>
        );
      }
      i++;
    }
    return elements;
  };

  if (loading) return <p style={{ textAlign: 'center', padding: '80px', color: '#4a7c59', fontFamily: 'Lato, sans-serif' }}>Loading...</p>;

  if (!post) {
    return (
      <div style={{ maxWidth: '700px', margin: '80px auto', padding: '0 16px', textAlign: 'center' }}>
        <h1 style={{ fontFamily: 'Playfair Display, serif', color: '#1e3d20' }}>Post not found</h1>
        <button onClick={() => navigate('/blog')} style={{ marginTop: '20px', background: 'linear-gradient(135deg, #2c4a2e, #4a7c59)', color: '#f5f0e8', border: 'none', borderRadius: '20px', padding: '12px 24px', cursor: 'pointer', fontFamily: 'Lato, sans-serif', fontWeight: 700 }}>
          Back to Blog
        </button>
      </div>
    );
  }

  // Structured data so Google can show this as an article rich result
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.subtitle || post.title,
    image: post.image_url || undefined,
    datePublished: post.created_at,
    author: { '@type': 'Organization', name: 'True Plant Value' },
    publisher: { '@type': 'Organization', name: 'True Plant Value' },
  };

  return (
    <div style={{ maxWidth: '760px', margin: '0 auto', padding: '40px 16px 80px' }}>
      <SEOMeta
        title={post.title}
        description={post.subtitle || post.title}
        image={post.image_url}
        url={`/blog/${slug}`}
        type="article"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <button onClick={() => navigate('/blog')} style={{
        background: 'transparent', border: 'none', color: '#4a7c59',
        fontFamily: 'Lato, sans-serif', fontSize: '13px', fontWeight: 700,
        cursor: 'pointer', marginBottom: '32px', padding: '0',
      }}>← Back to Blog</button>

      <div style={{ marginBottom: '32px' }}>
        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          {post.category && (
            <span style={{ background: '#f0f7f1', color: '#4a7c59', padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', fontFamily: 'Lato, sans-serif' }}>
              {post.category}
            </span>
          )}
          <span style={{ fontSize: '13px', color: '#8b9e8c', fontFamily: 'Lato, sans-serif' }}>
            {new Date(post.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </span>
        </div>
        {post.image_url ? (
          <img src={post.image_url} alt={post.title} style={{ width: '100%', maxHeight: '300px', objectFit: 'cover', borderRadius: '16px', marginBottom: '24px' }} />
        ) : (
          <div style={{ fontSize: '56px', marginBottom: '16px' }}>{post.emoji || '🌿'}</div>
        )}
        <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: '36px', color: '#1e3d20', lineHeight: 1.2, marginBottom: '12px' }}>
          {post.title}
        </h1>
        {post.subtitle && (
          <p style={{ color: '#4a5e4b', fontFamily: 'Lato, sans-serif', fontSize: '18px', lineHeight: 1.6 }}>
            {post.subtitle}
          </p>
        )}
      </div>

      <div style={{ background: '#ffffff', borderRadius: '20px', padding: '40px', boxShadow: '0 2px 16px rgba(44,74,46,0.08)', border: '1px solid rgba(143,185,154,0.3)' }}>
        {renderContent(post.content)}
      </div>

      <div style={{ background: 'linear-gradient(135deg, #2c4a2e, #4a7c59)', borderRadius: '20px', padding: '32px', marginTop: '32px', textAlign: 'center' }}>
        <div style={{ fontSize: '36px', marginBottom: '12px' }}>🌿</div>
        <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '24px', color: '#f5f0e8', marginBottom: '12px' }}>
          See Real Plant Prices
        </h3>
        <p style={{ color: '#8fb99a', fontFamily: 'Lato, sans-serif', fontSize: '14px', marginBottom: '20px' }}>
          Search actual sold prices from our community database. Free forever.
        </p>
        <button onClick={() => navigate('/search')} style={{ background: '#f5f0e8', color: '#2c4a2e', border: 'none', borderRadius: '20px', padding: '12px 28px', fontSize: '14px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'Lato, sans-serif' }}>
          Search Plant Prices →
        </button>
      </div>
    </div>
  );
};

export default BlogPost;